/* eslint-disable no-underscore-dangle */
import { useEffect, useMemo, useRef, useState } from "react";

import { useLang } from "@hooks/useLang";
import {
  useCreateAnalyticsFilterMutation,
  useGetAnalyticsFilterQuery,
  useUpdateAnalyticsFilterMutation,
  useDeleteAnalyticsFilterMutation,
  useUpdateFilterNameNotSaveMutation,
  FilterType,
  Order,
  toFilter,
  REG_SINGLE_LOCATION_IGNORE
} from "@queries/tanstack/analytics-filter/AnalyticsFilterQuery";
import _ from "lodash";
import { IconNoResult, IconPictogram } from "@resources/icons/Icons";

import { useLocationRuleGroupByRuleType } from "@hooks/analytics/useLocationRuleGroupByRuleType";
import { AnalyticsFilterRequestDto } from "@open-api";
import { HVDropdown, HVScrollBar, useHVSnackbar } from "@repo/HVComponents";
import { useLocationListByDeviceId } from "@hooks/analytics/useLocationListByDeviceId";
import { DEFAULT_SELECTED_ANALYTICS_LOCATION } from "@resources/Constants";
import GenerativeAI from "@components/generative-ai";
import { useRuleGroupByLocation } from "@hooks/analytics/useRuleGroupByLocation";
import { LeftMenu } from "./menu/LeftMenu";
import * as S from "./AnalyticsPage.styled";
import { AnalyticsPageSkeleton } from "./skeleton/AnalyticsPageSkeleton";
import { OrderingButton } from "./menu/btns/OrderingButton";
import {
  MAX_LOCATION_COUNT,
  MAX_RULE_IN_ONE_LOCATION_COUNT
} from "./rule/AnalyticsRuleTree";
import ChartList from "./chart/ChartList";

export const AnalyticsPage = () => {
  const { translate } = useLang();

  const [searchFilter, setSearchFilter] = useState<FilterType>();
  const [scrolled, setScrolled] = useState(false);
  const [aiState, setAIState] = useState<{ isShow: boolean; title: string }>({
    isShow: false,
    title: ""
  });
  const {
    data: filters,
    isLoading: isFilterLoading,
    isInitialLoading: isInitialLoadingFilter
  } = useGetAnalyticsFilterQuery();
  const [filterIdx, setFilterIdx] = useState<number>();
  const showIndex = (() => {
    // if have saved filter, show last filter when first enter
    if (_.isUndefined(filterIdx)) {
      if (filters.length === 0) {
        return 0;
      }

      // find last updated filter
      const lastUpdatedIdx = (() => {
        const lastUpdated = _.maxBy(
          filters.filter((filter) => !!filter.updatedAt),
          (filtered) => new Date(filtered.updatedAt!).getTime()
        );

        if (!lastUpdated) {
          return 0;
        }

        return filters.findIndex((filter) => filter._id === lastUpdated._id);
      })();

      return lastUpdatedIdx;
    }
    return filterIdx;
  })();

  const { mutateAsync: updateFilter } = useUpdateAnalyticsFilterMutation();
  const { mutateAsync: createFilter } = useCreateAnalyticsFilterMutation();
  const { mutateAsync: deleteFilter } = useDeleteAnalyticsFilterMutation();
  const { mutateAsync: updateFilterNameNotSave } =
    useUpdateFilterNameNotSaveMutation();

  const mounted = useRef(false);
  const { showSnackbar } = useHVSnackbar();
  // [TODO] state 처리되는 데이터 랜더링 최적화 해야함. ( 한번에 처리되도록 )
  const { data: ruleGroup, isLoading: isLoadingRuleGroup } =
    useLocationRuleGroupByRuleType(
      searchFilter?.rules ?? filters?.[0]?.rules ?? []
    );

  const { data: locationList } = useLocationListByDeviceId({
    deviceIds: _.map(searchFilter?.rules, (rule) => rule.split(":")[1])
  });

  const ruleGroupByRuleType = useRuleGroupByLocation({
    rules: searchFilter?.rules ?? []
  });

  const dropdownList = [
    DEFAULT_SELECTED_ANALYTICS_LOCATION,
    ...(locationList ?? [])
  ].map((v) => {
    return {
      label: v.name,
      value: v.id
    };
  });

  const [selectedLocationId, setSelectedLocationId] = useState(
    DEFAULT_SELECTED_ANALYTICS_LOCATION.id
  );

  useEffect(() => {
    if (isInitialLoadingFilter) {
      return;
    }

    if (filters.length > 0 && !mounted.current) {
      setSearchFilter(filters[showIndex]);
      mounted.current = true;
    }
  }, [isInitialLoadingFilter]);

  const handleSave = (
    type: "SAVE" | "SAVE_AS",
    options: { fileName: string; id?: string }
  ) => {
    if (searchFilter?.rules && searchFilter?.baseTime) {
      const rules = searchFilter.rules.map((rule) => {
        const [ruleType, deviceId, channel, ruleName] = (rule as string).split(
          ":"
        );
        return {
          deviceId,
          ruleName,
          channel: Number(channel),
          ruleType
        };
      });
      const filterName = options.fileName;
      const startDateTime = searchFilter.baseTime.start;
      const endDateTime = searchFilter.baseTime.end;
      const calenderMode = searchFilter.baseTime.mode;
      const order = searchFilter.order;
      const objectType = searchFilter.objectType;
      const updatedAt = searchFilter.updatedAt;

      const reqParams = {
        filterName,
        startDateTime,
        endDateTime,
        rules,
        order,
        calenderMode,
        objectType,
        updatedAt
      } as unknown as AnalyticsFilterRequestDto;

      if (filters.length === 0 || type === "SAVE_AS") {
        createFilter(reqParams)
          .then((v) => {
            showSnackbar({
              id: "analytics-result-saved",
              message: translate("analytics_result_saved"),
              variant: "online-success"
            });

            const nextIdx = filters.length;
            setFilterIdx(nextIdx);
            setSearchFilter(toFilter(v));
            setAIState({ isShow: false, title: "" });
          })
          .catch(() => {
            showSnackbar({
              id: "request-error",
              message: translate("request_error"),
              variant: "fail"
            });
          });
      } else {
        const filterId = options.id;
        if (filterId) {
          updateFilter({
            ...reqParams,
            filterId
          })
            .then(() => {
              showSnackbar({
                id: "analytics-result-saved",
                message: translate("analytics_result_saved"),
                variant: "online-success"
              });
            })
            .catch(() => {
              showSnackbar({
                id: "request-error",
                message: translate("request_error"),
                variant: "fail"
              });
            });
        }
      }
    }
  };

  const disableSubfilter = useMemo(() => {
    return !searchFilter || (searchFilter && searchFilter.order.length === 0);
  }, [searchFilter]);

  const isSingleLocation = useMemo(() => {
    return (
      selectedLocationId !== DEFAULT_SELECTED_ANALYTICS_LOCATION.id ||
      (locationList && locationList.length === 1)
    );
  }, [selectedLocationId, locationList]);

  const handleRemove = (id: string) => {
    const next = (() => {
      // 1) if just have one filter, set initial state
      if (filters.length === 1) {
        return {
          search: undefined,
          idx: 0
        };
      }

      // 2) if update target is current filter, move first filter
      if (filters[showIndex] && filters[showIndex]._id === id) {
        const ridx = 0; // return index
        const tmpFilter = filters.filter((filter) => filter._id !== id)[ridx];
        return {
          search: _.cloneDeep(tmpFilter),
          idx: ridx
        };
      }

      // 3) if update target is other filter, keep current filter
      const showId = filters[showIndex]._id;
      const nextFilters = filters.filter((filter) => filter._id !== id);
      const nextIdx = nextFilters.findIndex((filter) => filter._id === showId);

      return {
        search: nextFilters[nextIdx],
        idx: nextIdx
      };
    })();

    deleteFilter(id)
      .then(() => {
        setFilterIdx(next.idx);
        setSelectedLocationId(DEFAULT_SELECTED_ANALYTICS_LOCATION.id);
        setSearchFilter(next.search);
        setAIState({ isShow: false, title: "" });
      })
      .then(() => {
        showSnackbar({
          id: "analytics-result-removed",
          message: translate("analytics_result_removed"),
          variant: "online-success"
        });
      })
      .catch(() => {
        showSnackbar({
          id: "request-error",
          message: translate("request_error"),
          variant: "fail"
        });
      });
  };
  const handleChangeFilterName = (filterId: string, filterName: string) => {
    const prevFilter = filters.find((filter) => filter._id === filterId);
    if (prevFilter && prevFilter.rules && prevFilter.baseTime) {
      // temporary apply UI before API is completed
      const prevFilterName = prevFilter.filterName;
      updateFilterNameNotSave({ filterId, filterName });
      if (searchFilter?._id === filterId) {
        setSearchFilter({ ...searchFilter, filterName } as FilterType);
      }
      const rules = prevFilter.rules.map((rule) => {
        const [ruleType, deviceId, channel, ruleName] = (rule as string).split(
          ":"
        );
        return {
          deviceId,
          ruleName,
          channel: Number(channel),
          ruleType
        };
      });

      const startDateTime = prevFilter.baseTime.start;
      const endDateTime = prevFilter.baseTime.end;
      const calenderMode = prevFilter.baseTime.mode;
      const order = prevFilter.order;
      const objectType = prevFilter.objectType;
      const updatedAt = prevFilter.updatedAt;

      const reqParams = {
        startDateTime,
        endDateTime,
        rules,
        order,
        calenderMode,
        filterName,
        objectType,
        updatedAt
      } as unknown as AnalyticsFilterRequestDto;

      updateFilter({
        ...reqParams,
        filterId
      }).catch(() => {
        // restore when fail
        updateFilterNameNotSave({ filterId, filterName: prevFilterName });
        setSearchFilter({
          ...searchFilter,
          filterName: prevFilterName
        } as FilterType);
      });
    }
  };

  const handleChangeOrder = (options: { order: Order[] }) => {
    const { order } = options;
    setSearchFilter({
      ...searchFilter,
      order
    } as FilterType);
  };

  const handleChangeTab = (id: string) => {
    const idx = filters.findIndex((item) => item._id === id);
    setFilterIdx(idx);
    setSelectedLocationId(DEFAULT_SELECTED_ANALYTICS_LOCATION.id);
    setSearchFilter(filters[idx]);
  };

  const handleShowChat = (title: string) => {
    setAIState({
      isShow: !!title,
      title
    });
  };

  const handleSearch = (v: FilterType) => {
    setSearchFilter(v);
    setSelectedLocationId(DEFAULT_SELECTED_ANALYTICS_LOCATION.id);
  };

  if (
    isFilterLoading ||
    isLoadingRuleGroup ||
    (filters.length > 0 && !searchFilter)
  ) {
    return <AnalyticsPageSkeleton />;
  }

  return (
    <S.Container>
      <S.FloatWrap>
        <LeftMenu
          // key={String(typeof filters[showIndex]?._id === "undefined")}
          savedFilters={filters}
          savedFilterIdx={showIndex}
          searchedFilter={searchFilter}
          onSearch={handleSearch}
          onSave={handleSave}
          onChangeTab={handleChangeTab}
          onRemove={handleRemove}
          onChangeFilterName={handleChangeFilterName}
        />
      </S.FloatWrap>

      <S.Content
        style={{
          marginLeft: "296px",
          marginRight: aiState.isShow ? "380px" : "0",
          width: aiState.isShow ? "calc(100% - 680px)" : "100%",
          height: "calc(100vh - 68px)"
        }}
      >
        <S.FilterNameWrapper scrolled={scrolled}>
          <S.FilterName>
            {searchFilter?.filterName ?? translate("filter_name")}
          </S.FilterName>
        </S.FilterNameWrapper>
        {(() => {
          return (
            <HVScrollBar
              aria-label="scrollbar"
              scrollBarProps={{
                h: "calc(100vh - 76px)",
                scrollbars: "y",
                type: "auto",
                p: 0,
                w: "100%",
                onScrollPositionChange: (e) => {
                  const nextScrolled = e.y > 0;
                  if (scrolled !== nextScrolled) {
                    setScrolled(nextScrolled);
                  }
                }
              }}
            >
              <S.DropDownAndListWrap>
                <S.ChartFilterWrap>
                  <HVDropdown
                    w={200}
                    h={32}
                    aria-label="location dropdown"
                    type="medium"
                    options={dropdownList}
                    selectedItem={selectedLocationId}
                    getSelectedValue={(value) => {
                      if (value) {
                        setSelectedLocationId(value as string);
                      }
                    }}
                    selectWithArrow={false}
                    disabled={
                      disableSubfilter || (locationList?.length ?? 0) <= 1
                    }
                    styles={{
                      input: {
                        borderRadius: 4,
                        borderColor: "var(--hv-input-outline-default)",
                        background: "var(--hv-input-foreground-default)",
                        fontSize: 14,
                        fontWeight: 400,
                        color: "var(--hv-text-normal-secondary)"
                      },
                      section: {
                        width: 12,
                        marginRight: 10
                      },
                      option: {
                        fontSize: 14
                      }
                    }}
                  />
                  {(() => {
                    const searchedOrder =
                      (searchFilter && searchFilter.order) ?? [];
                    return (
                      <OrderingButton
                        key={searchFilter && `${searchFilter.order.join("")}}`}
                        disabled={disableSubfilter}
                        order={searchedOrder}
                        disableOrder={(() => {
                          if (isSingleLocation) {
                            return searchedOrder.filter((v) =>
                              REG_SINGLE_LOCATION_IGNORE.test(v)
                            );
                          }

                          return [];
                        })()}
                        onSave={handleChangeOrder}
                      />
                    );
                  })()}
                </S.ChartFilterWrap>
                {(() => {
                  if (_.isNil(searchFilter)) {
                    return (
                      <S.NoticeWrap>
                        <IconNoResult />
                        <S.NoticeTitile>
                          {translate("analytics_search")}
                        </S.NoticeTitile>
                      </S.NoticeWrap>
                    );
                  }
                  const isExceedLocationCount =
                    locationList && locationList.length > MAX_LOCATION_COUNT;

                  if (isExceedLocationCount) {
                    return (
                      <S.NoticeWrap>
                        <IconPictogram />
                        <S.NoticeTitile>
                          {translate("analytics_no_data")}
                        </S.NoticeTitile>
                        <S.NoticeMessage>
                          {translate(
                            "analytics_unable_data_limit_location"
                          ).replace(/{max}/g, `${MAX_LOCATION_COUNT}`)}
                        </S.NoticeMessage>
                      </S.NoticeWrap>
                    );
                  }

                  const isExceedRuleInOneLocationCount = (() => {
                    const locationName = (() => {
                      // 1) if selected location is only one in rule-tree
                      if (locationList && locationList.length === 1) {
                        return locationList[0].name;
                      }

                      // 2) if select a location from dropdown
                      if (
                        locationList &&
                        selectedLocationId !==
                          DEFAULT_SELECTED_ANALYTICS_LOCATION.id
                      ) {
                        return locationList.find(
                          (location) => location.id === selectedLocationId
                        )?.name;
                      }

                      return undefined;
                    })();

                    // check rule count in selected location
                    if (locationName) {
                      return (
                        ruleGroupByRuleType.data?.[locationName] &&
                        ruleGroupByRuleType.data[locationName].length >
                          MAX_RULE_IN_ONE_LOCATION_COUNT
                      );
                    }

                    return false;
                  })();

                  if (isExceedRuleInOneLocationCount) {
                    return (
                      <S.NoticeWrap>
                        <IconPictogram />
                        <S.NoticeTitile>
                          {translate("analytics_no_data")}
                        </S.NoticeTitile>
                        <S.NoticeMessage>
                          {translate(
                            "analytics_unable_data_limit_rule"
                          ).replace(
                            /{max}/g,
                            `${MAX_RULE_IN_ONE_LOCATION_COUNT}`
                          )}
                        </S.NoticeMessage>
                      </S.NoticeWrap>
                    );
                  }

                  const isNoData = _.isEmpty(ruleGroup);
                  if (isNoData) {
                    return (
                      <S.NoticeWrap>
                        <IconPictogram />
                        <S.NoticeTitile>
                          {translate("analytics_no_data")}
                        </S.NoticeTitile>
                        <S.NoticeMessage>
                          {translate("analytics_not_match")}
                        </S.NoticeMessage>
                      </S.NoticeWrap>
                    );
                  }

                  return (
                    <ChartList
                      handleShowChat={handleShowChat}
                      searchFilterBaseTime={searchFilter?.baseTime}
                      searchFilterOrder={searchFilter?.order}
                      isSingleLocation={isSingleLocation ?? false}
                      listKey={`${searchFilter._id}_${selectedLocationId}`}
                      ruleGroup={ruleGroup}
                      dropdownList={dropdownList}
                      selectedLocationId={selectedLocationId}
                    />
                  );
                })()}
              </S.DropDownAndListWrap>
            </HVScrollBar>
          );
        })()}
      </S.Content>
      {aiState.isShow && (
        <S.GenAI isVisible={aiState.isShow}>
          <GenerativeAI
            title={aiState.title}
            searchFilter={searchFilter}
            ruleGroup={ruleGroup}
            isShow={aiState.isShow}
            selectedLocationId={selectedLocationId}
            onChangeInsightChat={handleShowChat}
          />
        </S.GenAI>
      )}
    </S.Container>
  );
};
